import { useEffect, useRef, useState } from "react";
import { sendInfoWanrSeenCart } from "~/features/cart/cart/api/sendInfoWanrSeenCart";
import { type RootState } from "~/app/store";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCartEntries,
  selectPackageEntryMap,
  selectTotalCartPrice,
  selectTotalCartPriceWithoutDiscount,
  selectTotalCartWeight,
  selectTotalItemCount,
} from "~/features/cart/cart/redux/selectors/cartSelectors";
import { type GroupedCart, type GroupedCartEntry } from "~/features/cart/cart/models/CartData";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import Spinner from "~/common/components/Spinner/Spinner";
import ProductTableV2 from "~/features/cart/cartV2/ProductTable";
import { OrderSummary } from "~/features/cart/cartV2/OrderSummary";
import FullscreenLoader from "~/features/loading/FullscreenLoader";
import ToastMessage from "~/common/components/Messages/ToastMessage/ToastMessage";
import { PunchoutCancelRequisition, PunchoutPlaceRequisition } from "~/features/cart/cart/Punchout/PunchoutButtons";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { CART_PNA_THRESHOLD } from "~/utils/constants";
import { CheckoutButton } from "~/features/cart/cart/components/Buttons/CheckoutButton";
import { deleteSectionSaga } from "~/features/cart/cart/redux/actions/cartQuoteCommonActions";
import CheckMessagesComponent from "~/common/components/CheckMessagesComponent";
import DisclaimerCountryOrigin from "~/domains/product/components/DisclaimerCountryOrigin";
import { selectCart } from "~/features/cart/cart/redux/selectors/groupedCartSelectors";
import { IncludesSurchargeDisclaimer } from "~/domains/pricing/components/IncludesSurchargeDisclaimer";
import { useTranslation } from "react-i18next";
import { useCartPageContext } from "~/features/cart/cart/CartPageContext";
import { IsBundledQuoteContextProvider } from "~/features/cart/cart/IsBundledQuoteContext";
import {
  selectCartContainsSurcharge,
  selectCartContainsTariffSurcharge,
  selectCartProductAmount,
  selectCartSurcharge,
} from "~/features/cart/cart/redux/selectors/selectors";
import { Box, Stack } from "~/common/mui";
import {
  selectAccountNumber,
  selectCartContainsHousingKIT,
  selectDisableCheckout,
  selectIsDstError,
  selectIsPnaLoading,
  selectIsRequestInFlight,
} from "~/features/cart/cart/redux/selectors/cartPageSelectors";
import { isPunchoutEnabled } from "~/features/cart/cart/Punchout/punchoutConstants";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import { Alert } from "~/common/mui/Alert/Alert";
import {
  switchProductVersion,
  updatePriceReferenceForCart as updatePriceReference,
} from "~/features/cart/cart/redux/actions/cartPageActions";
import { Snackbar, SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";
import { CartQuickActionsButton } from "~/features/cart/cart/components/Buttons/CartQuickActionsButton";
import { useAlternateProducts } from "~/features/cart/cart/AlternateProductsContext";
import ConnectedCPDProgress from "~/features/cms/homepagePersonalization/cpd/components/cpdProgress/ConnectedCPDProgress";
import type { ProductEntryListRef } from "~/features/cart/cartV2/ProductEntryList";
import { CartSectionTitle } from "../../checkout/components/CartSectionTitle";

/**
 * This variant of the cart page is used by:
 *
 * - Punchout
 * - B2B aka "Customers"
 */
export const B2BCartPage = () => {
  const { t } = useTranslation();
  const { recentlyAddedItem, setRecentlyAddedItem, scrollIndex, setScrollIndex } = useAlternateProducts();

  const cartData = useSelector(selectCart);
  const { disableCheckout: externalDisableCheckout } = useCartPageContext();
  const disableCheckout = useSelector(selectDisableCheckout(externalDisableCheckout));
  const requestInFlight = useSelector(selectIsRequestInFlight);

  const accountNumber = useSelector(selectAccountNumber);

  const productAmount = useSelector(selectCartProductAmount);
  const isPnaLoading = useSelector(selectIsPnaLoading);

  const totalWeight = useSelector(selectTotalCartWeight);
  const totalSum = useSelector(selectTotalCartPrice);
  const totalDiscount = useSelector(selectTotalCartPriceWithoutDiscount);
  const containsHousingKits = useSelector(selectCartContainsHousingKIT);

  const showPnALoading = isPnaLoading && productAmount <= CART_PNA_THRESHOLD;

  const scrollToRefs = useRef<Dict<ProductEntryListRef>>({});

  const invalidItemIndexes = useSelector((state: RootState) => getInvalidItemData(cartData, state));
  const invalidLineItemAmount = invalidItemIndexes.length;

  const webservicesDisabled = window.SCHA.config.disableAllWS;

  const [snackbarData, setSnackbarData] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });

  const dispatch = useDispatch();
  const deleteSection = (quoteNumber?: string) => {
    dispatch(deleteSectionSaga(quoteNumber || ""));
  };

  const hasAnySurcharge = useSelector(selectCartContainsSurcharge);
  const hasAnyTariffSurcharge = useSelector(selectCartContainsTariffSurcharge);
  const surchargeValue = useSelector(selectCartSurcharge);

  const packageEntryMap = useSelector(selectPackageEntryMap);
  const packageEntryIds = Object.keys(packageEntryMap).map(Number);
  const cartEntries = useSelector(selectCartEntries);
  const updatePriceReferenceForCart = (priceReference: string) =>
    dispatch(updatePriceReference(priceReference, packageEntryIds, handleOpenPriceRefSnackBar));
  const switchProductVersionForCart = () => dispatch(switchProductVersion());

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const snackbarValue = urlParams.get("switched-products-count");

    if (snackbarValue) {
      handleOpenSwitchProductSnackBar(parseInt(snackbarValue));
      removeUrlParam("switched-products-count");
    }
  }, []);

  function getcartEntryIdByCode(productCode: string) {
    const entries = Object.values(packageEntryMap);
    return entries.find((product) => product.productCode === productCode)?.cartEntryId;
  }

  useEffect(() => {
    if (recentlyAddedItem) {
      setSnackbarData({
        open: true,
        message: t("cartPage.addProduct.success.snackbar"),
      });

      const cartEntryID = getcartEntryIdByCode(recentlyAddedItem.code);
      const index = cartEntries.findIndex((item) => item === cartEntryID);

      const scrollRef = scrollToRefs.current["orderables"];
      scrollRef.scrollToIndex(index);

      const timer = setTimeout(() => {
        setRecentlyAddedItem(null);
      }, 2500);

      return () => clearTimeout(timer);
    }
  }, [recentlyAddedItem, cartData.orderables.entries.length]);

  const removeUrlParam = (key: string) => {
    const url = new URL(window.location.href);
    url.searchParams.delete(key);
    window.history.replaceState({}, "", url);
  };

  const handleOpenPriceRefSnackBar = (count: number) => {
    setSnackbarData({
      open: true,
      message: t("cartPage.modal.choosePriceReferenceForCart.snackbar", { count }),
    });
  };

  const handleOpenSwitchProductSnackBar = (count: number) => {
    setSnackbarData({
      open: true,
      message: t("cartPage.modal.switchProductVersion.snackbar", { count }),
    });
  };

  const checkOutBtnSection = (
    <>
      {!isPunchoutEnabled() && <CheckoutButton disabled={disableCheckout} />}
      {isPunchoutEnabled() && (
        <>
          <PunchoutCancelRequisition />
          <PunchoutPlaceRequisition disabled={disableCheckout} />
        </>
      )}
    </>
  );

  const ButtonSection = (
    <Stack gap={1} direction={"row"}>
      <CartQuickActionsButton
        disableReportDownload={isPnaLoading}
        switchProductVersionForCart={switchProductVersionForCart}
        updatePriceReferenceForCart={updatePriceReferenceForCart}
      />
      {checkOutBtnSection}
    </Stack>
  );

  const isCPDProgressEnabled = window.SCHA.feature.cpdBannerCart;

  const totalItemCount = useSelector(selectTotalItemCount);

  return (
    <>
      <DstErrorMessage />
      <Snackbar
        open={snackbarData.open}
        message={snackbarData.message}
        onClose={() => {
          setSnackbarData((prev) => ({ ...prev, open: false }));
          setRecentlyAddedItem(null);
        }}
        action={<SnackbarCloseButton onClick={() => setSnackbarData((prev) => ({ ...prev, open: false }))} />}
      />
      <Stack gap={3}>
        <PageTitle tag="div" className="mb-0">
          <div style={{ marginBottom: "16px" }} className="d-flex flex-justify-between flex-align-center">
            <div>{t("cartPage.title", { accNo: accountNumber, count: totalItemCount })}</div>
            <div data-test-id={"cart-action-bar"}>{ButtonSection}</div>
          </div>
        </PageTitle>

        {showPnALoading && <Spinner />}

        {invalidLineItemAmount > 0 && (
          <CheckMessagesComponent
            data-test-id={"cart-issue-with-product"}
            msgType={"error"}
            msgCount={invalidLineItemAmount}
            onScrollToMessage={(index) => {
              const data = invalidItemIndexes[index];
              scrollToRefs.current![data.tableName].scrollToIndex(data.index);
            }}
            sticky={true}
          />
        )}

        <ProductTableV2
          group={cartData.orderables}
          editMode={true}
          onClear={deleteSection}
          data-test-id={"cart-section-regular"}
          scrollRef={(ref) => (scrollToRefs.current!["orderables"] = ref!)}
        />

        {cartData.bundles.map((bundle, index) => (
          <IsBundledQuoteContextProvider value={true}>
            <CartSectionTitle
              title={t("page.cart.bundledQuote.title", {
                amount: bundle.entries.length,
                quoteNumber: bundle.quoteNumber,
              })}
              subtitle={t("page.cart.bundledQuote.subtitle")}
            />
            <ProductTableV2
              group={bundle}
              editMode={true}
              onClear={() => deleteSection(bundle.quoteNumber)}
              data-test-id={`cart-section-bundle-${bundle.quoteNumber}`}
              scrollRef={(ref) => (scrollToRefs.current![index] = ref!)}
            />
          </IsBundledQuoteContextProvider>
        ))}
      </Stack>

      <Box sx={{ display: "flex", flexFlow: "column", alignItems: "flex-end", my: 4 }}>
        <OrderSummary sx={{ justifySelf: "flex-end" }}>
          <OrderSummary.DataContainer>
            <OrderSummary.WeightRow weight={totalWeight} />
            <OrderSummary.SurchargeRow
              surcharge={hasAnySurcharge && totalSum && totalSum.value > 0 ? surchargeValue : undefined}
            />
            <OrderSummary.DiscountRow discount={totalDiscount} />
            <OrderSummary.TotalRow
              value={webservicesDisabled ? null : totalSum}
              showPriceOnRequest
              asterisk={hasAnySurcharge || hasAnyTariffSurcharge}
            />
            <OrderSummary.TaxDisclaimer />
          </OrderSummary.DataContainer>
          <OrderSummary.Divider />
          <OrderSummary.Actions>{checkOutBtnSection}</OrderSummary.Actions>
          {isCPDProgressEnabled && (
            <ConnectedCPDProgress totalCartValue={totalSum?.value} sx={{ mt: "24px", width: "100%" }} />
          )}
        </OrderSummary>
      </Box>

      <Stack gap={2}>
        <Alert severity="info">
          <div data-test-id={"info-disclaimer"}>{t("cartPage.disclaimer")}</div>
        </Alert>
        <DisclaimerCountryOrigin />
        {containsHousingKits && (
          <Stack gap={2}>
            <Alert severity="info">
              <div data-test-id={"info-housing-disclaimer-1"}>{t("cartPage.housingDisclaimer.1")}</div>
            </Alert>
            <Alert severity="info">
              <div data-test-id={"info-housing-disclaimer-2"}>{t("cartPage.housingDisclaimer.2")}</div>
            </Alert>
          </Stack>
        )}
        {<IncludesSurchargeDisclaimer hasSurcharge={hasAnySurcharge} tariffSurcharge={hasAnyTariffSurcharge} />}
      </Stack>

      {requestInFlight && <FullscreenLoader />}
    </>
  );
};

const DstErrorMessage = () => {
  const { t } = useTranslation();
  const { val, setTrue, setFalse } = useBooleanState(false);
  const dstError = useSelector(selectIsDstError);
  useEffect(() => {
    if (dstError) {
      setTrue();
      sendInfoWanrSeenCart();
    }
  }, [dstError]);

  return (
    <>
      {val && (
        <ToastMessage type={"warning"} onClose={() => setFalse()}>
          {t("cartPage.warnMsg.dst")}
        </ToastMessage>
      )}
    </>
  );
};

const getInvalidItemData = (cartData: GroupedCart, state: RootState) => {
  const invalidEntries: { tableName: string; index: number }[] = [];
  const makeEntryProcessor = (tableName: string) => (entry: GroupedCartEntry, index: number) => {
    if (
      entry.packGroups.length > 0 &&
      entry.packGroups.some(
        (pack) =>
          pack.packEntryIds.length > 0 &&
          pack.packEntryIds.some((id) => {
            const lineItem = state.cart.packEntriesById[id];
            const productDescriptor = selectProductDescriptor(state, lineItem);
            return !lineItem.valid || productDescriptor.isError;
          }),
      )
    ) {
      invalidEntries.push({ tableName, index });
    }
  };
  cartData.orderables.entries.forEach(makeEntryProcessor("orderables"));
  cartData.bundles.forEach((bundle, bundleIndex) => bundle.entries.forEach(makeEntryProcessor("" + bundleIndex)));
  return invalidEntries;
};
